import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Download, Calendar, ChevronDown, TrendingUp, TrendingDown,
  IndianRupee, Receipt, Printer, Gauge, Undo2, TicketPercent, Wallet,
  Clock, Bug, WifiOff, AlertTriangle, FileWarning, Monitor, Check,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart,
} from 'recharts';
import { API, useAuth } from '../App';
import ProfitabilityTab from './ProfitabilityTab';

/* ---------- palette (vibrant, not tacky) ---------- */
const C = {
  violet: '#8B5CF6', amber: '#F59E0B', emerald: '#10B981', pink: '#EC4899', cyan: '#06B6D4',
};
const CHART_COLORS = [C.violet, C.amber, C.emerald, C.pink, C.cyan];
const HEAD = { fontFamily: "'Outfit', 'Manrope', sans-serif" };

/* ---------- IST date math ---------- */
const DAY = 86400000;
const IST_MS = 330 * 60000;
const istMidnight = (offsetDays = 0) => {
  const d = new Date(Date.now() + IST_MS);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + offsetDays) - IST_MS;
};
const istMonthStart = (monthOffset = 0) => {
  const d = new Date(Date.now() + IST_MS);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + monthOffset, 1) - IST_MS;
};
const istDateStr = (ms) => new Date(ms + IST_MS).toISOString().slice(0, 10);

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This week' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
];

function computeRange(preset, customFrom, customTo) {
  const t0 = istMidnight(0);
  switch (preset) {
    case 'today':
      return { start: t0, end: t0 + DAY, prevStart: t0 - DAY, prevEnd: t0, granularity: 'hour', compareLabel: 'yesterday' };
    case 'yesterday':
      return { start: t0 - DAY, end: t0, prevStart: t0 - 2 * DAY, prevEnd: t0 - DAY, granularity: 'hour', compareLabel: 'day before' };
    case '7d':
      return { start: t0 - 6 * DAY, end: t0 + DAY, prevStart: t0 - 13 * DAY, prevEnd: t0 - 6 * DAY, granularity: 'day', compareLabel: 'previous 7 days' };
    case '30d':
      return { start: t0 - 29 * DAY, end: t0 + DAY, prevStart: t0 - 59 * DAY, prevEnd: t0 - 29 * DAY, granularity: 'day', compareLabel: 'previous 30 days' };
    case 'this_month': {
      const s = istMonthStart(0);
      return { start: s, end: t0 + DAY, prevStart: istMonthStart(-1), prevEnd: s, granularity: 'day', compareLabel: 'last month' };
    }
    case 'last_month': {
      const s = istMonthStart(-1), e = istMonthStart(0);
      return { start: s, end: e, prevStart: istMonthStart(-2), prevEnd: s, granularity: 'day', compareLabel: 'month before' };
    }
    case 'this_week': {
      const dow = new Date(t0 + IST_MS).getUTCDay();
      const s = t0 - ((dow + 6) % 7) * DAY; // Monday IST
      return { start: s, end: t0 + DAY, prevStart: s - 7 * DAY, prevEnd: s, granularity: 'day', compareLabel: 'last week' };
    }
    case 'ytd': {
      const d = new Date(Date.now() + IST_MS);
      const s = Date.UTC(d.getUTCFullYear(), 0, 1) - IST_MS;
      const e = t0 + DAY;
      const ps = Date.UTC(d.getUTCFullYear() - 1, 0, 1) - IST_MS;
      return { start: s, end: e, prevStart: ps, prevEnd: ps + (e - s), granularity: 'day', compareLabel: 'same period last year' };
    }
    case 'all': {
      const s = Date.UTC(2020, 0, 1) - IST_MS;
      return { start: s, end: t0 + DAY, prevStart: null, prevEnd: null, granularity: 'month', compareLabel: null };
    }
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const s = Date.parse(`${customFrom}T00:00:00+05:30`);
      const e = Date.parse(`${customTo}T00:00:00+05:30`) + DAY;
      if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
      const span = e - s;
      return { start: s, end: e, prevStart: s - span, prevEnd: s, granularity: span <= DAY ? 'hour' : 'day', compareLabel: 'previous period' };
    }
    default:
      return null;
  }
}

/* ---------- formatting ---------- */
const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const num = (n) => (n || 0).toLocaleString('en-IN');

const DeltaChip = ({ cur, prev, invert = false, testid }) => {
  if (prev == null) return null;
  const pct = prev === 0 ? (cur > 0 ? 100 : 0) : ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  const good = invert ? !up || pct === 0 : up;
  if (cur === 0 && prev === 0) {
    return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-500" data-testid={testid}>—</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${good ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
      data-testid={testid}
    >
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(pct) >= 999 ? '999+' : Math.abs(pct).toFixed(0)}%
    </span>
  );
};

const ChartTooltip = ({ active, payload, label, money = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
      <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-600 capitalize">{p.name || p.dataKey}:</span>
          <span className="font-semibold text-slate-900">{money && (p.dataKey === 'revenue' || p.dataKey === 'prev_revenue') ? inr(p.value) : num(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const KPI_DEFS = [
  { key: 'revenue', label: 'Revenue', icon: IndianRupee, money: true, accent: 'from-violet-500 to-purple-500', bg: 'bg-violet-50', fg: 'text-violet-600',
    tip: 'Gross revenue: every successful payment (incl. late payments) from ALL booths — including deleted/old booths — in the selected window. Refunds are NOT deducted here (see Net Revenue). The PnL tab counts only booths currently in your booth list and uses its own date window, so its gross figure can differ.' },
  { key: 'paid_orders', label: 'Paid Orders', icon: Receipt, accent: 'from-cyan-500 to-sky-500', bg: 'bg-cyan-50', fg: 'text-cyan-600' },
  { key: 'prints', label: 'Prints Sold', icon: Printer, accent: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', fg: 'text-amber-600' },
  { key: 'aov', label: 'Avg Order Value', icon: Gauge, money: true, accent: 'from-pink-500 to-rose-500', bg: 'bg-pink-50', fg: 'text-pink-600' },
  { key: 'refunds_amount', label: 'Refunds', icon: Undo2, money: true, invert: true, accent: 'from-rose-500 to-red-500', bg: 'bg-rose-50', fg: 'text-rose-600' },
  { key: 'voucher_discount', label: 'Voucher Discounts', icon: TicketPercent, money: true, accent: 'from-fuchsia-500 to-pink-500', bg: 'bg-fuchsia-50', fg: 'text-fuchsia-600' },
  { key: 'net_revenue', label: 'Net Revenue', icon: Wallet, money: true, accent: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-50', fg: 'text-emerald-600',
    tip: 'Gross revenue minus refunds. Razorpay fees, GST, rent and costs are NOT deducted — see the PnL tab for true profit.' },
];

const Card = ({ title, subtitle, children, className = '', testid, right }) => (
  <div className={`bg-white border border-slate-200 rounded-xl shadow-sm p-6 ${className}`} data-testid={testid}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900" style={HEAD}>{title}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);

/* ---------- booth multi-select ---------- */
const BoothPicker = ({ kiosks, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const label = selected.length === 0 ? 'All booths' : selected.length === 1
    ? (kiosks.find((k) => k.id === selected[0])?.name || '1 booth')
    : `${selected.length} booths`;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="analytics-booth-filter"
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${selected.length ? 'bg-violet-50 border-violet-300 text-violet-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
      >
        <Monitor className="w-4 h-4" /> {label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2 max-h-72 overflow-auto" data-testid="analytics-booth-dropdown">
          <button
            onClick={() => onChange([])}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-slate-50 text-left font-medium text-slate-700"
            data-testid="analytics-booth-all"
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected.length === 0 ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
              {selected.length === 0 && <Check className="w-3 h-3 text-white" />}
            </span>
            All booths
          </button>
          <div className="h-px bg-slate-100 my-1" />
          {kiosks.map((k) => (
            <button
              key={k.id}
              onClick={() => toggle(k.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-slate-50 text-left text-slate-700"
              data-testid={`analytics-booth-option-${k.id}`}
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected.includes(k.id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
                {selected.includes(k.id) && <Check className="w-3 h-3 text-white" />}
              </span>
              <span className="truncate">{k.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- main page ---------- */
export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('today');
  const [tab, setTab] = useState('performance');
  const [customFrom, setCustomFrom] = useState(istDateStr(istMidnight(-7)));
  const [customTo, setCustomTo] = useState(istDateStr(istMidnight(0)));
  const [selectedKiosks, setSelectedKiosks] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });
  const [exporting, setExporting] = useState(false);
  const [compareMode, setCompareMode] = useState('lastweek'); // prev | lastweek (default: same time last week)

  const baseRange = useMemo(() => computeRange(preset, customFrom, customTo), [preset, customFrom, customTo]);
  // "Same time last week": shift the comparison window back exactly 7 days.
  // Only meaningful for spans of a week or less (Friday vs last Friday).
  const canCompareLastWeek = !!baseRange && (baseRange.end - baseRange.start) <= 7 * DAY;
  const range = useMemo(() => {
    if (!baseRange) return baseRange;
    if (compareMode === 'lastweek' && canCompareLastWeek) {
      return {
        ...baseRange,
        prevStart: baseRange.start - 7 * DAY,
        prevEnd: baseRange.end - 7 * DAY,
        compareLabel: 'same time last week',
      };
    }
    return baseRange;
  }, [baseRange, compareMode, canCompareLastWeek]);

  const fetchData = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: new Date(range.start).toISOString(),
        end: new Date(range.end).toISOString(),
        granularity: range.granularity,
      });
      if (range.prevStart != null && range.prevEnd != null) {
        params.set('prev_start', new Date(range.prevStart).toISOString());
        params.set('prev_end', new Date(range.prevEnd).toISOString());
      }
      if (selectedKiosks.length) params.set('kiosk_ids', selectedKiosks.join(','));
      const res = await axios.get(`${API}/analytics/overview?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load analytics');
    }
    setLoading(false);
  }, [range, selectedKiosks, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    axios.get(`${API}/kiosks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setKiosks(r.data.map((k) => ({ id: k.id, name: k.name }))))
      .catch(() => {});
  }, [token]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        format: 'csv',
        start_date: istDateStr(range.start),
        end_date: istDateStr(range.end - DAY),
      });
      if (selectedKiosks.length === 1) params.set('kiosk_id', selectedKiosks[0]);
      const res = await axios.get(`${API}/analytics/revenue/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `piczoo-analytics-${istDateStr(range.start)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { /* noop */ }
    setExporting(false);
  };

  const cur = data?.kpis?.current;
  const prev = data?.kpis?.previous;
  const funnel = data?.funnel;
  const boothRows = useMemo(() => {
    const rows = [...(data?.booth_table || [])];
    rows.sort((a, b) => {
      const av = a[sort.key] ?? -1, bv = b[sort.key] ?? -1;
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [data, sort]);

  const setSortKey = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const rangeText = !range ? '' : preset === 'all'
    ? 'all data on record'
    : `${istDateStr(range.start)}${range.end - range.start > DAY ? ` → ${istDateStr(range.end - DAY)}` : ''}`;

  return (
    <div className="space-y-6 -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8 min-h-screen bg-slate-50" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={HEAD}>Analytics</h1>
            <div className="flex gap-1 bg-slate-100 rounded-full p-1" data-testid="analytics-tabs">
              {[['performance', 'Performance'], ['profitability', 'Profitability']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === id ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`analytics-tab-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-1" data-testid="analytics-range-label">
            {PRESETS.find((p) => p.id === preset)?.label} · {rangeText} (IST){prev != null && range?.compareLabel && ` · compared to ${range.compareLabel}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting || !range}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 backdrop-blur-xl bg-white/80 border-y border-slate-200" data-testid="analytics-filter-bar">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs font-semibold uppercase tracking-wider">
            <Calendar className="w-4 h-4" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${preset === p.id ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                data-testid={`analytics-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2" data-testid="analytics-custom-range">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white" data-testid="analytics-custom-from" />
              <span className="text-slate-400 text-xs">to</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white" data-testid="analytics-custom-to" />
            </div>
          )}
          {canCompareLastWeek && (
            <div className="flex gap-1 bg-slate-100 rounded-full p-1" data-testid="compare-mode-toggle">
              {[['prev', 'vs previous'], ['lastweek', 'vs last week']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setCompareMode(id)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${compareMode === id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`compare-mode-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto">
            <BoothPicker kiosks={kiosks} selected={selectedKiosks} onChange={setSelectedKiosks} />
          </div>
        </div>
      </div>

      {error && <div className="text-rose-600 text-sm bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="analytics-error">{error}</div>}

      {tab === 'profitability' ? (
        <ProfitabilityTab token={token} range={range} selectedKiosks={selectedKiosks} kiosks={kiosks} />
      ) : loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {KPI_DEFS.map((k) => (
            <div key={k.key} className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse h-28" />
          ))}
        </div>
      ) : data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="analytics-kpi-row">
            {KPI_DEFS.map((k) => {
              const Icon = k.icon;
              return (
                <div key={k.key} title={k.tip || undefined} className="relative overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm p-4 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={`kpi-${k.key}`}>
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${k.accent}`} />
                  <div className="flex items-center justify-between mb-2">
                    <span className={`p-1.5 rounded-lg ${k.bg}`}><Icon className={`w-4 h-4 ${k.fg}`} /></span>
                    <DeltaChip cur={cur?.[k.key] || 0} prev={prev ? prev[k.key] : null} invert={k.invert} testid={`kpi-${k.key}-delta`} />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 tracking-tight" style={HEAD} data-testid={`kpi-${k.key}-value`}>
                    {k.money ? inr(cur?.[k.key]) : num(cur?.[k.key])}
                  </div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">
                    {k.label}{k.tip && <span className="ml-1 normal-case text-slate-300 cursor-help" data-testid={`kpi-${k.key}-tip`}>ⓘ</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fair-fight callout — live range compared to the same elapsed time */}
          {data?.kpis?.previous_so_far && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="fair-fight-callout">
              <Clock className="w-5 h-5 text-violet-500 shrink-0" />
              <div className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">So far: {inr(cur?.revenue)}</span>
                {' '}— by this time {range?.compareLabel === 'same time last week' ? 'last week' : range?.compareLabel === 'yesterday' ? 'yesterday' : `in the ${range?.compareLabel || 'previous period'}`} you were at{' '}
                <span className="font-semibold text-slate-900">{inr(data.kpis.previous_so_far.revenue)}</span>
              </div>
              <DeltaChip cur={cur?.revenue || 0} prev={data.kpis.previous_so_far.revenue} testid="fair-fight-delta" />
            </div>
          )}

          {/* Trend + booth comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" title="Revenue trend" subtitle={range?.granularity === 'hour' ? 'By hour (IST)' : range?.granularity === 'month' ? 'By month (IST)' : 'By day (IST)'} testid="chart-revenue-trend">
              <ResponsiveContainer width="100%" height={280}>
                {range?.granularity === 'hour' ? (
                  <ComposedChart data={data.trend}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval={2} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                    <Bar dataKey="revenue" fill={C.violet} radius={[6, 6, 0, 0]} maxBarSize={28} />
                    {prev != null && (
                      <Line type="monotone" dataKey="prev_revenue" name={range.compareLabel || 'previous'} stroke="#94A3B8" strokeWidth={2} strokeDasharray="6 4" dot={false} />
                    )}
                  </ComposedChart>
                ) : (
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.violet} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={C.violet} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="revenue" stroke={C.violet} strokeWidth={2.5} fill="url(#colorRev)" />
                    <Area type="monotone" dataKey="orders" stroke={C.cyan} strokeWidth={1.5} fill="none" strokeDasharray="4 3" />
                    {prev != null && (
                      <Area type="monotone" dataKey="prev_revenue" name={range.compareLabel || 'previous'} stroke="#94A3B8" strokeWidth={2} fill="none" strokeDasharray="6 4" />
                    )}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </Card>
            <Card title="Booth vs booth" subtitle="Revenue in period" testid="chart-booth-comparison">
              {data.by_kiosk.length === 0 ? (
                <EmptyChart label="No paid orders in this period" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.by_kiosk} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} width={90} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                    <Bar dataKey="revenue" radius={[0, 6, 6, 0]} maxBarSize={22}>
                      {data.by_kiosk.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Booth comparison over time — colour-coded line per booth */}
          {data.trend_by_kiosk?.kiosks?.length > 0 && (
            <BoothTrendCard trendByKiosk={data.trend_by_kiosk} granularity={range.granularity} />
          )}

          {/* Time analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Revenue by hour of day" subtitle="When customers pay (IST) — plan staffing & promos" testid="chart-hour-of-day">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.hour_of_day}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval={2} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `₹${v}`} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                  <Bar dataKey="revenue" fill={C.amber} radius={[5, 5, 0, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Day-of-week performance" subtitle="Best and slowest days" testid="chart-day-of-week">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.day_of_week}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `₹${v}`} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                  <Bar dataKey="revenue" fill={C.cyan} radius={[5, 5, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Funnel + copies mix */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card
              className="lg:col-span-2"
              title="Payment funnel"
              subtitle="Where customers drop off between scanning and paying"
              testid="payment-funnel"
              right={funnel?.avg_time_to_pay_secs != null && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
                  <Clock className="w-3 h-3" /> avg {Math.round(funnel.avg_time_to_pay_secs)}s to pay
                </span>
              )}
            >
              {funnel && <Funnel funnel={funnel} />}
            </Card>
            <Card title="Copies mix" subtitle="1–4 copies popularity" testid="chart-copies-mix">
              {data.copies_mix.length === 0 ? (
                <EmptyChart label="No paid orders in this period" />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie data={data.copies_mix} dataKey="orders" nameKey="copies" innerRadius={55} outerRadius={85} paddingAngle={3} strokeWidth={0}>
                        {data.copies_mix.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={({ active, payload }) => active && payload?.length ? (
                        <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
                          <span className="font-semibold text-slate-900">{payload[0].payload.copies} {payload[0].payload.copies === 1 ? 'copy' : 'copies'}</span>
                          <div className="text-slate-600 text-xs mt-0.5">{payload[0].payload.orders} orders · {inr(payload[0].payload.revenue)}</div>
                        </div>
                      ) : null} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {data.copies_mix.map((c, i) => (
                      <div key={c.copies} className="flex items-center gap-2 text-xs" data-testid={`copies-mix-${c.copies}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-slate-600">{c.copies}× copies</span>
                        <span className="ml-auto font-semibold text-slate-900">{c.orders}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>

          {/* Price-point conversion — does the shown price convert? */}
          {(data.price_points || []).length > 0 && (
            <Card title="Price-point conversion" subtitle="How each price you showed actually converted — your pricing truth table" className="p-0 overflow-hidden" testid="price-point-table">
              <div className="px-6 pt-6" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-y border-slate-100 bg-slate-50/60">
                      {['Price / print', 'Reached payment', 'Paid', 'Conversion', ''].map((h, i) => (
                        <th key={h || i} className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.price_points.map((p) => (
                      <tr key={p.unit_price} data-testid={`price-point-${p.unit_price}`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">{inr(p.unit_price)}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{num(p.reached)}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{num(p.paid)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${p.conversion_pct >= 60 ? 'text-emerald-600' : p.conversion_pct >= 35 ? 'text-amber-600' : 'text-rose-600'}`}>{p.conversion_pct}%</td>
                        <td className="px-4 py-3 w-1/3 min-w-[120px]">
                          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(2, p.conversion_pct)}%`, background: p.conversion_pct >= 60 ? '#10B981' : p.conversion_pct >= 35 ? '#F59E0B' : '#F43F5E' }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Ops health strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ops-health-strip">
            <OpsStat icon={Bug} color="text-rose-600" bg="bg-rose-50" label="Crashes" value={data.ops.crashes} />
            <OpsStat icon={FileWarning} color="text-amber-600" bg="bg-amber-50" label="Booth errors logged" value={data.ops.activity_errors} />
            <OpsStat icon={WifiOff} color="text-slate-600" bg="bg-slate-100" label="Offline alerts" value={data.ops.offline_alerts} />
            <OpsStat icon={AlertTriangle} color="text-orange-600" bg="bg-orange-50" label="Print-fail alerts" value={data.ops.print_fail_alerts} />
          </div>
          {data.ops.low_paper_booths.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="low-paper-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-sm font-medium text-amber-800">Low paper:</span>
              {data.ops.low_paper_booths.map((b) => (
                <button key={b.kiosk_id} onClick={() => navigate(`/admin/kiosks/${b.kiosk_id}`)}
                  className="text-xs font-semibold bg-white border border-amber-300 text-amber-800 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors">
                  {b.name} · {b.paper_remaining} left
                </button>
              ))}
            </div>
          )}

          {/* Booth performance table */}
          <Card title="Booth performance" subtitle="Every booth, ranked — click a column to sort" className="p-0 overflow-hidden" testid="booth-performance-table">
            <div className="px-6 pt-6" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-slate-100 bg-slate-50/60">
                    {[
                      ['name', 'Booth', 'left'], ['revenue', 'Revenue', 'right'], ['orders', 'Orders', 'right'],
                      ['conversion_pct', 'Conv %', 'right'],
                      ['prints', 'Prints', 'right'], ['aov', 'AOV', 'right'], ['paper_remaining', 'Paper', 'right'],
                      ['crashes', 'Crashes', 'right'], ['online', 'Status', 'left'],
                    ].map(([key, label, align]) => (
                      <th key={key}
                        onClick={() => setSortKey(key)}
                        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-slate-800 ${align === 'right' ? 'text-right' : 'text-left'}`}
                        data-testid={`booth-sort-${key}`}
                      >
                        {label}{sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {boothRows.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-10 text-slate-400">No booths</td></tr>
                  ) : boothRows.map((b) => (
                    <tr key={b.kiosk_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/kiosks/${b.kiosk_id}`)} data-testid={`booth-row-${b.kiosk_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{b.name}</div>
                        {b.location && <div className="text-xs text-slate-400">{b.location}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{inr(b.revenue)}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{num(b.orders)}</td>
                      <td className="px-4 py-3 text-right" data-testid={`booth-conv-${b.kiosk_id}`}>
                        {b.conversion_pct == null ? <span className="text-xs text-slate-400">—</span> : (
                          <span className={`font-semibold ${b.conversion_pct >= 60 ? 'text-emerald-600' : b.conversion_pct >= 35 ? 'text-amber-600' : 'text-rose-600'}`}>{b.conversion_pct}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{num(b.prints)}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{b.orders ? inr(b.aov) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {b.paper_remaining == null ? <span className="text-xs text-slate-400">—</span> : (
                          <span className={`font-mono font-semibold ${b.paper_remaining <= 50 ? 'text-rose-600' : b.paper_remaining <= 100 ? 'text-amber-600' : 'text-emerald-600'}`}>{b.paper_remaining}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {b.crashes > 0 ? <span className="text-rose-600 font-semibold">{b.crashes}</span> : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                          <span className={`w-2 h-2 rounded-full ${b.online ? 'bg-emerald-500' : 'bg-rose-400'}`} />
                          <span className={b.online ? 'text-emerald-700' : 'text-rose-600'}>{b.online ? 'Online' : 'Offline'}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Digital engagement — QR scans & photo downloads */}
          {data.digital && (
            <Card title="Digital engagement" subtitle="Customers who scanned the QR and downloaded their photos" testid="digital-engagement-section">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <MiniStat label="Photo sessions" value={num(data.digital.sessions)} color="text-slate-700" />
                <MiniStat label="QR scanned" value={num(data.digital.scanned)} color="text-sky-600" />
                <MiniStat label="Scan rate" value={`${data.digital.scan_rate}%`} color="text-sky-600" />
                <MiniStat label="Downloaded" value={num(data.digital.downloaded)} color="text-emerald-600" />
                <MiniStat label="Download rate" value={`${data.digital.download_rate}%`} color="text-emerald-600" />
              </div>
              <p className="text-xs text-slate-400 mt-3">
                {num(data.digital.total_downloads)} total downloads (strips + individual photos). Tracking started when this feature went live — older sessions aren&apos;t counted.
              </p>
            </Card>
          )}

          {/* Voucher impact */}
          <Card title="Voucher impact" subtitle="How discount codes drive sales" testid="voucher-impact-section">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <MiniStat label="Orders with voucher" value={num(data.vouchers.orders_with)} color="text-fuchsia-600" />
              <MiniStat label="Revenue via vouchers" value={inr(data.vouchers.revenue_with)} color="text-violet-600" />
              <MiniStat label="Discount given" value={inr(data.vouchers.discount_given)} color="text-amber-600" />
              <MiniStat label="Full-price revenue" value={inr(data.vouchers.revenue_without)} color="text-emerald-600" />
            </div>
            {data.vouchers.top_codes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-y border-slate-100 bg-slate-50/60">
                      <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Code</th>
                      <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Uses</th>
                      <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Revenue</th>
                      <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Discount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.vouchers.top_codes.map((c) => (
                      <tr key={c.code} className="hover:bg-slate-50" data-testid={`voucher-code-row-${c.code}`}>
                        <td className="px-4 py-2.5 font-mono font-semibold text-violet-700">{c.code}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{num(c.orders)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{inr(c.revenue)}</td>
                        <td className="px-4 py-2.5 text-right text-amber-600">{inr(c.discount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No voucher redemptions in this period</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

const EmptyChart = ({ label }) => (
  <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">{label}</div>
);

const TREND_METRICS = [
  { id: 'revenue', label: 'Revenue', money: true },
  { id: 'orders', label: 'Orders', money: false },
  { id: 'prints', label: 'Prints', money: false },
];

const BoothTrendCard = ({ trendByKiosk, granularity }) => {
  const [metric, setMetric] = useState('revenue');
  const { kiosks, series } = trendByKiosk;
  const money = metric === 'revenue';
  const nameOf = Object.fromEntries(kiosks.map((k) => [k.kiosk_id, k.name]));
  return (
    <Card
      title="Booth performance over time"
      subtitle={`Colour-coded per booth · ${granularity === 'hour' ? 'by hour (IST)' : granularity === 'month' ? 'by month (IST)' : 'by day (IST)'}${kiosks.length === 6 ? ' · top 6 booths by revenue' : ''}`}
      testid="chart-booth-trend"
      right={(
        <div className="flex gap-1">
          {TREND_METRICS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${metric === m.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-800'}`}
              data-testid={`booth-trend-metric-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={series}>
          <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval={granularity === 'hour' ? 2 : 'preserveStartEnd'} />
          <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => (money ? `₹${v}` : v)} />
          <Tooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
                <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
                {payload.map((p) => (
                  <div key={p.dataKey} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: p.stroke }} />
                    <span className="text-slate-600">{nameOf[String(p.dataKey).split('.')[0]] || p.dataKey}:</span>
                    <span className="font-semibold text-slate-900">{money ? inr(p.value) : num(p.value)}</span>
                  </div>
                ))}
              </div>
            );
          }} />
          {kiosks.map((k, i) => (
            <Line
              key={k.kiosk_id}
              type="monotone"
              dataKey={`${k.kiosk_id}.${metric}`}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 flex-wrap mt-3" data-testid="booth-trend-legend">
        {kiosks.map((k, i) => (
          <span key={k.kiosk_id} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <span className="w-3 h-1 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
            {k.name}
          </span>
        ))}
      </div>
    </Card>
  );
};

const OpsStat = ({ icon: Icon, color, bg, label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex items-center gap-3" data-testid={`ops-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <span className={`p-2 rounded-lg ${bg}`}><Icon className={`w-4 h-4 ${color}`} /></span>
    <div>
      <div className="text-xl font-bold text-slate-900" style={HEAD}>{num(value)}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  </div>
);

const MiniStat = ({ label, value, color }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
    <div className={`text-lg font-bold ${color}`} style={HEAD}>{value}</div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
  </div>
);

const FUNNEL_COLORS = { paid: '#10B981', created: '#8B5CF6', abandoned: '#F59E0B', failed: '#EF4444', cancelled: '#94A3B8' };

const Funnel = ({ funnel }) => {
  const { created, paid, status_counts: sc, conversion_pct, lost_revenue, prev } = funnel;
  const nonTerminal = Math.max(0, created - paid - (sc.abandoned || 0) - (sc.failed || 0) - (sc.cancelled || 0) - (sc.refunded || 0));
  const stages = [
    { label: 'Orders created', value: created, color: FUNNEL_COLORS.created },
    { label: 'Paid', value: paid, color: FUNNEL_COLORS.paid },
  ];
  const dropoffs = [
    { label: 'Abandoned', value: sc.abandoned || 0, color: FUNNEL_COLORS.abandoned },
    { label: 'Failed', value: sc.failed || 0, color: FUNNEL_COLORS.failed },
    { label: 'Cancelled', value: sc.cancelled || 0, color: FUNNEL_COLORS.cancelled },
    { label: 'Pending', value: nonTerminal, color: '#CBD5E1' },
  ].filter((d) => d.value > 0);
  return (
    <div>
      <div className="space-y-3">
        {stages.map((s) => (
          <div key={s.label} data-testid={`funnel-stage-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-600 font-medium">{s.label}</span>
              <span className="font-bold text-slate-900" style={HEAD}>{num(s.value)}</span>
            </div>
            <div className="h-6 rounded-lg bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-lg transition-[width] duration-500"
                style={{ width: created ? `${Math.max(2, (s.value / created) * 100)}%` : '0%', background: s.color }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-bold" data-testid="funnel-conversion">
          {conversion_pct}% conversion
          {prev && <DeltaChip cur={conversion_pct} prev={prev.conversion_pct} testid="funnel-conversion-delta" />}
        </span>
        <div className="flex items-center gap-3 flex-wrap">
          {(lost_revenue || 0) > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="funnel-lost-revenue">
              Lost revenue: <b className="text-rose-600">{inr(lost_revenue)}</b>
            </span>
          )}
          {dropoffs.map((d) => (
            <span key={d.label} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-full" style={{ background: d.color }} /> {d.label}: <b className="text-slate-700">{num(d.value)}</b>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
